/**
 * Post-job flow: the pure, platform-agnostic decision layer (PRD §7).
 *
 * Mirrors the create-cv machine pattern: no React, no I/O — just the state
 * model, the step order, every §7.2–§7.5 validation rule, the AI-draft and
 * edit-mode mappings, and the payload assembly for the v2 employer job
 * contract. `use-post-job-flow.ts` wires this to routing/mutations and the
 * step components render it, so native and web cannot drift apart.
 *
 * i18n: the machine never holds copy. Validators return `messageKey`s and the
 * default screening questions store prompt/option KEYS (employer namespace);
 * the hook resolves them with `t` at render/submit time.
 */
import type {
  CompanyJobEmployment,
  CompanyJobWorkType,
  JobDefaultQuestionKey,
  JobDistribution,
  JobEligibilityExperience,
  JobEligibilityLanguage,
  JobEligibilityLocation,
  JobEligibilityWorkEligibility,
  JobNationalityRequirement,
  JobScreeningQuestionType,
  JobScreeningQuestionV2,
} from '@tabbio/types';
import { isBoardDistribution, resolveJobDistribution } from '@tabbio/types';
import type {
  CompanyJobWithV2,
  CreateEmployerJobInput,
  EmployerJobBasicsInput,
  EmployerJobCompensationInput,
  EmployerJobContentInput,
  EmployerJobEligibilityInput,
  EmployerJobSeniority,
  GenerateCompanyJobDraftResultWithV2,
  UpdateEmployerJobInput,
} from '../../../lib/employer-jobs-contract';
import {
  CITY_OPTIONS,
  COUNTRY_OPTIONS,
  DEPARTMENT_OPTIONS,
} from '../post-job-flow.data';

// ── Vocabulary ───────────────────────────────────────────────────────────────

export type PostJobStep =
  | 'entry'
  | 'basics'
  | 'requirements'
  | 'applications'
  | 'review';

export const POST_JOB_STEPS: readonly PostJobStep[] = [
  'entry',
  'basics',
  'requirements',
  'applications',
  'review',
];

/** Entry is a pre-step; the visible wizard is basics → review. */
export const POST_JOB_PROGRESS_STEPS: readonly PostJobStep[] = [
  'basics',
  'requirements',
  'applications',
  'review',
];

export const POST_JOB_STEP_COUNT = POST_JOB_PROGRESS_STEPS.length;

export const SENIORITY_VALUES: readonly EmployerJobSeniority[] = [
  'entry',
  'mid',
  'senior',
  'lead',
  'executive',
];

/**
 * Candidate-location choices, in the order the picker shows them.
 *
 * These are country-relative: `in_country` / `in_city` / `in_region` read
 * against whatever country and city the job itself is filed under, so the same
 * five options work for a Cairo role and a Riyadh one. `in_city` is dropped by
 * the step when the job has no city (a remote role, say).
 */
export const CANDIDATE_LOCATION_VALUES: readonly JobEligibilityLocation[] = [
  'any',
  'in_country',
  'in_city',
  'in_region',
  'open_to_relocation',
];

/**
 * The UAE-specific ids that shipped before candidate location became
 * country-relative, folded onto their nearest live equivalent when an existing
 * job is opened for editing. `dubai`/`abu_dhabi` were emirate-level, so they
 * become `in_city` — the job's own city is what the label now names.
 */
const LEGACY_CANDIDATE_LOCATION_IDS: Record<string, JobEligibilityLocation> = {
  uae: 'in_country',
  dubai: 'in_city',
  abu_dhabi: 'in_city',
  gcc: 'in_region',
  uae_relocation: 'open_to_relocation',
};

/** Folds a stored candidate-location id onto the live set. */
export function normalizeCandidateLocation(
  value: string | null | undefined,
): JobEligibilityLocation {
  const trimmed = (value ?? '').trim();
  if (!trimmed) return 'any';
  const mapped = LEGACY_CANDIDATE_LOCATION_IDS[trimmed];
  if (mapped) return mapped;
  return CANDIDATE_LOCATION_VALUES.includes(trimmed as JobEligibilityLocation)
    ? (trimmed as JobEligibilityLocation)
    : 'any';
}

export const EXPERIENCE_YEARS_VALUES: readonly JobEligibilityExperience[] = [
  'any',
  '1_plus',
  '3_plus',
  '5_plus',
  '7_plus',
  '10_plus',
];

export const LANGUAGE_VALUES: readonly JobEligibilityLanguage[] = [
  'any',
  'english',
  'arabic',
  'english_and_arabic',
  'english_or_arabic',
];

export const WORK_ELIGIBILITY_VALUES: readonly JobEligibilityWorkEligibility[] =
  ['any', 'eligible', 'eligible_or_sponsorship', 'sponsorship_required'];

/**
 * Nationality choices. Every GCC nationality is listed because the localisation
 * quotas that make the question lawful (Emiratisation, Saudization, Qatarisation,
 * Omanisation, Bahrainisation, Kuwaitisation) each exist in their own country,
 * and `gcc` covers the schemes that treat any GCC national as local.
 */
export const NATIONALITY_VALUES: readonly JobNationalityRequirement[] = [
  'none',
  'emirati',
  'saudi',
  'qatari',
  'kuwaiti',
  'bahraini',
  'omani',
  'gcc',
  'other',
];

/** @deprecated Renamed to {@link NATIONALITY_VALUES}. */
export const WORKFORCE_PROGRAMME_VALUES = NATIONALITY_VALUES;

/**
 * The two programme-shaped ids that shipped before this field became a
 * nationality, folded onto the nationality each programme reserves roles for.
 */
const LEGACY_NATIONALITY_IDS: Record<string, JobNationalityRequirement> = {
  emiratisation: 'emirati',
  saudization: 'saudi',
};

/** Folds a stored nationality id onto the live set. */
export function normalizeNationality(
  value: string | null | undefined,
): JobNationalityRequirement {
  const trimmed = (value ?? '').trim();
  if (!trimmed) return 'none';
  const mapped = LEGACY_NATIONALITY_IDS[trimmed];
  if (mapped) return mapped;
  return NATIONALITY_VALUES.includes(trimmed as JobNationalityRequirement)
    ? (trimmed as JobNationalityRequirement)
    : 'none';
}

export const DISTRIBUTION_VALUES: readonly JobDistribution[] = [
  'tabbio_and_company',
  'tabbio_only',
  'company_page_only',
  'hidden',
];

/** Unknown/legacy values fail open to the board default, as the API does. */
export function normalizeDistribution(value: unknown): JobDistribution {
  return DISTRIBUTION_VALUES.includes(value as JobDistribution)
    ? (value as JobDistribution)
    : 'tabbio_and_company';
}

/**
 * Board-listed distributions and the masking rule come from the shared types
 * package, so the wizard cannot drift from what the API stores. Every entry
 * point here (radio, toggle, hydration, payload) runs state through
 * `resolveDistribution`, which is why the two controls can no longer disagree
 * about where the role appears.
 */
export { isBoardDistribution };
export const resolveDistribution = resolveJobDistribution;

/**
 * Apply a distribution choice, keeping the identity toggle consistent with it.
 *
 * Picking a place to list the role wins over a stale identity choice.
 * Confidential identity only exists for board-listed jobs (§7.5), and
 * "Tabbio + Company page" is the one board option a masked employer cannot
 * have: their own company page would name them. Choosing it therefore drops
 * the masking, which the employer sees as the toggle flipping off.
 *
 * The typed label survives either way, so an accidental tap does not make the
 * employer retype it.
 */
export function applyDistributionChoice(
  state: JobDraftState,
  distribution: JobDistribution,
): JobDraftState {
  const keepsConfidential =
    state.confidential.enabled &&
    isBoardDistribution(distribution) &&
    distribution !== 'tabbio_and_company';
  return {
    ...state,
    distribution,
    confidential: keepsConfidential
      ? state.confidential
      : { enabled: false, publicLabel: state.confidential.publicLabel },
  };
}

/**
 * Apply an identity change, moving the role off the company page if needed.
 *
 * Turning masking on used to leave "Tabbio + Company page" selected while the
 * server dropped the role from the company page anyway, so the screen asserted
 * the opposite of what shipped. The selection now moves to "Tabbio job board
 * only", where the employer can see it.
 */
export function applyConfidentialPatch(
  state: JobDraftState,
  patch: Partial<JobDraftState['confidential']>,
): JobDraftState {
  const requested = { ...state.confidential, ...patch };
  // Masking off the board fails closed rather than dragging the role onto it.
  // The screen only offers the toggle on a board distribution, so this is the
  // guard for a caller that gets it wrong: promoting a `hidden` role to the
  // board to satisfy the pairing rule would publish a role nobody asked to
  // publish, and leaving the pair as-is would fail review on the next step.
  const confidential =
    requested.enabled && !isBoardDistribution(state.distribution)
      ? { ...requested, enabled: false }
      : requested;
  return {
    ...state,
    distribution: resolveDistribution(state.distribution, confidential.enabled),
    confidential,
  };
}

export const QUESTION_TYPE_VALUES: readonly JobScreeningQuestionType[] = [
  'yes_no',
  'multiple_choice',
  'short_answer',
  'long_answer',
  'number',
];

/**
 * One currency per country the wizard can file a job under, plus the three
 * used for cross-border pay. EGP and JOD were missing while Egypt and Jordan
 * were selectable, which left those roles priced in the wrong money.
 */
export const SALARY_CURRENCIES: readonly string[] = [
  'AED',
  'SAR',
  'QAR',
  'OMR',
  'BHD',
  'KWD',
  'EGP',
  'JOD',
  'USD',
  'EUR',
  'GBP',
];

export const MIN_JOB_BRIEF_LENGTH = 20;
export const MIN_JOB_SUMMARY_LENGTH = 20;
export const MIN_WORKFORCE_NOTE_LENGTH = 20;
export const MIN_JOB_TITLE_LENGTH = 2;
export const MAX_JOB_TITLE_LENGTH = 160;
export const MAX_CONFIDENTIAL_LABEL_LENGTH = 80;

// ── State ────────────────────────────────────────────────────────────────────

export interface JobDraftEntryState {
  mode: 'ai' | 'manual' | null;
  brief: string;
}

export interface JobDraftBasicsState {
  title: string;
  /** Canonical department id, '' when unset. */
  department: string;
  /** Canonical country id ('uae', …), '' when unset. */
  country: string;
  /** Canonical city id ('dubai', …), '' when unset. */
  city: string;
  workMode: CompanyJobWorkType;
  employment: CompanyJobEmployment;
  seniority: EmployerJobSeniority;
  /**
   * Edit mode only: what the server actually had stored for
   * department/country/city when this draft was hydrated.
   *
   * The pickers can only show a selection for a canonical option id, so
   * hydration folds "Product" -> "product" and "UAE" -> "uae". Saving that id
   * back would rewrite a field the employer never touched — the public
   * careers page prints the department verbatim, so a no-op save turned
   * "Product · Dubai, UAE" into "product · Dubai, United Arab Emirates".
   * Keeping the original here lets `buildBasicsInput` send it back unchanged
   * unless the employer actually picked a different option.
   */
  stored?: {
    department: string;
    country: string;
    city: string;
  };
}

export interface JobDraftContentState {
  summary: string;
  responsibilities: string[];
  requirements: string[];
  niceToHave: string[];
  benefits: string[];
}

/** min/max kept as raw input strings so text fields bind directly. */
export interface JobDraftCompensationState {
  min: string;
  max: string;
  currency: string;
  period: 'monthly' | 'yearly';
  visible: boolean;
}

export interface JobDraftWorkforceState {
  value: JobNationalityRequirement;
  note: string;
  acknowledged: boolean;
}

export interface JobDraftEligibilityState {
  candidateLocation: JobEligibilityLocation;
  experienceYears: JobEligibilityExperience;
  language: JobEligibilityLanguage;
  workEligibility: JobEligibilityWorkEligibility;
  workforceProgramme: JobDraftWorkforceState;
}

export interface DefaultQuestionConfig {
  key: JobDefaultQuestionKey;
  enabled: boolean;
  required: boolean;
  type: JobScreeningQuestionType;
}

export interface JobDraftQuestionsState {
  defaults: DefaultQuestionConfig[];
  custom: JobScreeningQuestionV2[];
}

export interface JobDraftState {
  entry: JobDraftEntryState;
  basics: JobDraftBasicsState;
  content: JobDraftContentState;
  compensation: JobDraftCompensationState;
  eligibility: JobDraftEligibilityState;
  questions: JobDraftQuestionsState;
  distribution: JobDistribution;
  confidential: { enabled: boolean; publicLabel: string };
  lifecycleChoice: 'draft' | 'live';
}

// ── Default application questions (PRD §7.4) ─────────────────────────────────

export interface DefaultQuestionDef {
  key: JobDefaultQuestionKey;
  /** employer-namespace key for the candidate-facing prompt. */
  promptKey: string;
  /** employer-namespace keys for multiple-choice options. */
  optionKeys?: string[];
  compatibleTypes: JobScreeningQuestionType[];
  defaultType: JobScreeningQuestionType;
  defaultRequired: boolean;
  defaultEnabled: boolean;
}

const q = (key: string) => `jobFlow.questions.defaults.${key}`;

export const DEFAULT_QUESTION_DEFS: readonly DefaultQuestionDef[] = [
  {
    key: 'education',
    promptKey: q('education.prompt'),
    optionKeys: [
      q('education.options.highSchool'),
      q('education.options.diploma'),
      q('education.options.bachelors'),
      q('education.options.masters'),
      q('education.options.doctorate'),
      q('education.options.other'),
    ],
    compatibleTypes: ['multiple_choice', 'short_answer'],
    defaultType: 'multiple_choice',
    defaultRequired: true,
    defaultEnabled: true,
  },
  {
    key: 'experience_years',
    promptKey: q('experienceYears.prompt'),
    compatibleTypes: ['number', 'short_answer'],
    defaultType: 'number',
    defaultRequired: true,
    defaultEnabled: true,
  },
  {
    key: 'expected_salary',
    promptKey: q('expectedSalary.prompt'),
    compatibleTypes: ['number', 'short_answer'],
    defaultType: 'number',
    defaultRequired: false,
    defaultEnabled: true,
  },
  {
    key: 'availability',
    promptKey: q('availability.prompt'),
    optionKeys: [
      q('availability.options.immediate'),
      q('availability.options.oneMonth'),
      q('availability.options.twoMonths'),
      q('availability.options.threeMonths'),
      q('availability.options.moreThanThree'),
    ],
    compatibleTypes: ['multiple_choice', 'short_answer'],
    defaultType: 'multiple_choice',
    defaultRequired: true,
    defaultEnabled: true,
  },
];

export function defaultQuestionDef(
  key: JobDefaultQuestionKey,
): DefaultQuestionDef {
  const def = DEFAULT_QUESTION_DEFS.find((item) => item.key === key);
  if (!def) throw new Error(`Unknown default question key: ${key}`);
  return def;
}

function createDefaultQuestionConfigs(): DefaultQuestionConfig[] {
  return DEFAULT_QUESTION_DEFS.map((def) => ({
    key: def.key,
    enabled: def.defaultEnabled,
    required: def.defaultRequired,
    type: def.defaultType,
  }));
}

/** Stable id — defaults keep a deterministic id so edit round-trips cleanly. */
export function defaultQuestionId(key: JobDefaultQuestionKey): string {
  return `default_${key}`;
}

/** Stable unique id for a custom question (never positional). */
export function createQuestionId(): string {
  return `q_${Date.now().toString(36)}_${Math.random()
    .toString(36)
    .slice(2, 10)}`;
}

/**
 * Materialize the enabled §7.4 defaults as typed screening questions. The
 * machine stores i18n KEYS; `resolveText` (usually `t` bound to the employer
 * namespace) turns them into candidate-facing copy at submit time.
 */
export function assembleDefaultQuestions(
  defaults: DefaultQuestionConfig[],
  resolveText: (key: string) => string,
): JobScreeningQuestionV2[] {
  const byKey = new Map(defaults.map((config) => [config.key, config]));
  const questions: JobScreeningQuestionV2[] = [];

  for (const def of DEFAULT_QUESTION_DEFS) {
    const config = byKey.get(def.key);
    if (!config?.enabled) continue;
    const type = def.compatibleTypes.includes(config.type)
      ? config.type
      : def.defaultType;
    questions.push({
      id: defaultQuestionId(def.key),
      prompt: resolveText(def.promptKey),
      type,
      required: config.required,
      order: questions.length,
      ...(type === 'multiple_choice' && def.optionKeys
        ? { options: def.optionKeys.map(resolveText) }
        : {}),
      source: 'default',
      defaultKey: def.key,
    });
  }

  return questions;
}

// ── Initial state ────────────────────────────────────────────────────────────

export function createInitialJobDraftState(): JobDraftState {
  return {
    entry: { mode: null, brief: '' },
    basics: {
      title: '',
      department: '',
      country: 'uae',
      city: 'dubai',
      workMode: 'onsite',
      employment: 'full_time',
      seniority: 'senior',
    },
    content: {
      summary: '',
      responsibilities: [],
      requirements: [],
      niceToHave: [],
      benefits: [],
    },
    compensation: {
      min: '',
      max: '',
      currency: 'AED',
      period: 'monthly',
      visible: true,
    },
    eligibility: {
      candidateLocation: 'any',
      experienceYears: 'any',
      language: 'any',
      workEligibility: 'any',
      workforceProgramme: { value: 'none', note: '', acknowledged: false },
    },
    questions: {
      defaults: createDefaultQuestionConfigs(),
      custom: [],
    },
    distribution: 'tabbio_and_company',
    confidential: { enabled: false, publicLabel: '' },
    lifecycleChoice: 'live',
  };
}

// ── Small shared helpers ─────────────────────────────────────────────────────

/** "Meaningful" length: whitespace runs collapse before counting (§7.3). */
export function meaningfulLength(value: string): number {
  return value.replace(/\s+/g, ' ').trim().length;
}

function nonEmpty(items: readonly string[]): string[] {
  return items.map((item) => item.trim()).filter(Boolean);
}

function parseSalary(value: string): number | undefined | 'invalid' {
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  const parsed = Number(trimmed.replace(/,/g, ''));
  if (!Number.isFinite(parsed) || parsed <= 0) return 'invalid';
  return parsed;
}

/** Reorder helper shared by the list editors and the question builder. */
export function moveListItem<T>(list: readonly T[], from: number, to: number): T[] {
  if (from === to || from < 0 || to < 0 || from >= list.length || to >= list.length) {
    return [...list];
  }
  const next = [...list];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
}

// ── Validation (§7.2–§7.5, mirrored client-side) ─────────────────────────────

export interface PostJobFieldError {
  /** Focus target for the step component ('title', 'summary', 'question:<id>', …). */
  fieldId: string;
  /** employer-namespace i18n key ('jobFlow.errors.…'). */
  messageKey: string;
}

const err = (fieldId: string, key: string): PostJobFieldError => ({
  fieldId,
  messageKey: `jobFlow.errors.${key}`,
});

export interface ValidateStepOptions {
  /**
   * Edit mode only: the role's stored content is still exactly as it loaded.
   *
   * Jobs created before this wizard (or by the agent / admin paths) can have
   * an empty responsibilities or qualifications list. Re-imposing the create
   * rules on content the employer has not touched made those roles
   * uneditable — step 2 refused to advance and there was no Save, so changing
   * a job TITLE required inventing list content. When the content is
   * untouched it is also omitted from the PATCH (see `buildUpdatePayload`),
   * so nothing invalid is ever sent to the server.
   */
  contentUntouched?: boolean;
}

export function validateStep(
  state: JobDraftState,
  step: PostJobStep,
  options: ValidateStepOptions = {},
): PostJobFieldError[] {
  const errors: PostJobFieldError[] = [];

  if (step === 'entry') {
    if (!state.entry.mode) {
      errors.push(err('brief', 'modeRequired'));
    } else if (
      state.entry.mode === 'ai' &&
      meaningfulLength(state.entry.brief) < MIN_JOB_BRIEF_LENGTH
    ) {
      errors.push(err('brief', 'briefTooShort'));
    }
    return errors;
  }

  if (step === 'basics') {
    const title = state.basics.title.trim();
    if (!title) {
      errors.push(err('title', 'titleRequired'));
    } else if (
      title.length < MIN_JOB_TITLE_LENGTH ||
      title.length > MAX_JOB_TITLE_LENGTH
    ) {
      errors.push(err('title', 'titleLength'));
    }
    if (state.basics.workMode !== 'remote' && !state.basics.country) {
      errors.push(err('country', 'countryRequired'));
    }
    if (
      (state.basics.workMode === 'onsite' ||
        state.basics.workMode === 'hybrid') &&
      !state.basics.city
    ) {
      errors.push(err('city', 'cityRequired'));
    }
    return errors;
  }

  if (step === 'requirements') {
    if (options.contentUntouched) {
      return errors;
    }
    if (meaningfulLength(state.content.summary) < MIN_JOB_SUMMARY_LENGTH) {
      errors.push(err('summary', 'summaryTooShort'));
    }
    if (nonEmpty(state.content.responsibilities).length < 1) {
      errors.push(err('responsibilities', 'responsibilitiesRequired'));
    }
    if (nonEmpty(state.content.requirements).length < 1) {
      errors.push(err('requirements', 'requirementsRequired'));
    }
    return errors;
  }

  if (step === 'applications') {
    const min = parseSalary(state.compensation.min);
    const max = parseSalary(state.compensation.max);
    if (min === 'invalid') errors.push(err('salaryMin', 'salaryInvalid'));
    if (max === 'invalid') errors.push(err('salaryMax', 'salaryInvalid'));
    if (
      typeof min === 'number' &&
      typeof max === 'number' &&
      min > max
    ) {
      errors.push(err('salaryMin', 'salaryRange'));
    }

    for (const question of state.questions.custom) {
      if (question.prompt.trim().length < 2) {
        errors.push(err(`question:${question.id}`, 'questionPromptRequired'));
        continue;
      }
      if (question.type === 'multiple_choice') {
        const unique = new Set(
          nonEmpty(question.options ?? []).map((option) =>
            option.toLowerCase(),
          ),
        );
        if (unique.size < 2) {
          errors.push(err(`question:${question.id}`, 'questionOptionsRequired'));
        }
      }
    }

    const programme = state.eligibility.workforceProgramme;
    if (programme.value !== 'none') {
      if (meaningfulLength(programme.note) < MIN_WORKFORCE_NOTE_LENGTH) {
        errors.push(err('workforceNote', 'workforceNoteRequired'));
      }
      if (!programme.acknowledged) {
        errors.push(err('workforceAck', 'workforceAckRequired'));
      }
    }
    return errors;
  }

  // review
  if (state.confidential.enabled) {
    if (!isBoardDistribution(state.distribution)) {
      errors.push(err('distribution', 'confidentialDistribution'));
    }
    const label = state.confidential.publicLabel.trim();
    if (label.length < 2 || label.length > MAX_CONFIDENTIAL_LABEL_LENGTH) {
      errors.push(err('confidentialLabel', 'confidentialLabelRequired'));
    }
  }
  return errors;
}

export interface PostJobStepValidation {
  step: PostJobStep;
  errors: PostJobFieldError[];
}

/** First step (in flow order) that fails validation, or null when clean. */
export function firstInvalidStep(
  state: JobDraftState,
  steps: readonly PostJobStep[] = POST_JOB_STEPS,
  options: ValidateStepOptions = {},
): PostJobStepValidation | null {
  for (const step of steps) {
    const errors = validateStep(state, step, options);
    if (errors.length > 0) return { step, errors };
  }
  return null;
}

/** Structural equality for the content block — drives `contentUntouched`. */
export function isSameJobContent(
  a: JobDraftContentState,
  b: JobDraftContentState,
): boolean {
  const sameList = (left: string[], right: string[]) =>
    left.length === right.length &&
    left.every((item, index) => item === right[index]);
  return (
    a.summary === b.summary &&
    sameList(a.responsibilities, b.responsibilities) &&
    sameList(a.requirements, b.requirements) &&
    sameList(a.niceToHave, b.niceToHave) &&
    sameList(a.benefits, b.benefits)
  );
}

// ── AI draft mapping (§7.1) ──────────────────────────────────────────────────

function normalizeSeniority(value: string | undefined): EmployerJobSeniority {
  const head = (value ?? '').trim().split('_')[0] as EmployerJobSeniority;
  return SENIORITY_VALUES.includes(head) ? head : 'mid';
}

const LEGACY_SECTION_PATTERNS: {
  section: 'summary' | 'responsibilities' | 'requirements';
  pattern: RegExp;
}[] = [
  { section: 'summary', pattern: /^about( the)? role/i },
  { section: 'responsibilities', pattern: /^responsibilit/i },
  { section: 'requirements', pattern: /^(required qualifications|requirements)/i },
];

/**
 * Legacy fallback: the flat `generatedJD` markdown assembled by the server
 * ("## About the role / ## Responsibilities / ## Required qualifications").
 * Best-effort split back into structured content so the employer still lands
 * on an editable draft instead of an empty form.
 */
export function parseLegacyGeneratedJd(generatedJD: string): {
  summary: string;
  responsibilities: string[];
  requirements: string[];
} {
  const summaryLines: string[] = [];
  const responsibilities: string[] = [];
  const requirements: string[] = [];
  let section: 'summary' | 'responsibilities' | 'requirements' = 'summary';

  for (const rawLine of generatedJD.split('\n')) {
    const line = rawLine.trim();
    if (!line) continue;

    const heading = line.replace(/^#{1,4}\s*/, '');
    const matched = LEGACY_SECTION_PATTERNS.find(({ pattern }) =>
      pattern.test(heading),
    );
    if (matched && (line.startsWith('#') || heading === line)) {
      section = matched.section;
      // Pure heading lines carry no content.
      if (line.startsWith('#') || matched.pattern.test(line)) continue;
    }

    const bullet = line.replace(/^[-•*]\s*/, '');
    const isBullet = bullet !== line;

    if (section === 'responsibilities') {
      responsibilities.push(bullet);
    } else if (section === 'requirements') {
      requirements.push(bullet);
    } else if (!isBullet) {
      summaryLines.push(line);
    }
  }

  return {
    summary: summaryLines.join(' ').trim(),
    responsibilities: nonEmpty(responsibilities),
    requirements: nonEmpty(requirements),
  };
}

/**
 * Map a generate-draft result onto the editable draft. Prefers the structured
 * v2 shape (`contract: 'v2'`), falls back to the legacy flat fields. AI never
 * touches compensation, eligibility, distribution, or confidentiality — those
 * remain whatever the employer chose (§7.1 safety rules).
 */
export function applyAiDraft(
  state: JobDraftState,
  draft: GenerateCompanyJobDraftResultWithV2,
): JobDraftState {
  const isV2 = draft.contract === 'v2' && !!draft.basics && !!draft.content;

  const basics: JobDraftBasicsState = isV2
    ? {
        title: draft.basics!.title,
        department: draft.basics!.department ?? '',
        country: draft.basics!.country ?? '',
        city: draft.basics!.city ?? '',
        workMode: draft.basics!.workMode,
        employment: draft.basics!.employment,
        seniority: normalizeSeniority(draft.basics!.seniority),
      }
    : {
        title: draft.title,
        department: draft.department ?? '',
        country: draft.country ?? '',
        city: draft.city ?? '',
        workMode: draft.workType,
        employment: draft.employment,
        seniority: normalizeSeniority(draft.experienceLevel),
      };

  const content: JobDraftContentState = isV2
    ? {
        summary: draft.content!.summary,
        responsibilities: [...draft.content!.responsibilities],
        requirements: [...draft.content!.requirements],
        niceToHave: [...(draft.content!.niceToHave ?? [])],
        benefits: [...state.content.benefits],
      }
    : {
        ...parseLegacyGeneratedJd(draft.generatedJD ?? ''),
        niceToHave: [...state.content.niceToHave],
        benefits: [...state.content.benefits],
      };

  let custom = state.questions.custom;
  const suggested = isV2 ? draft.suggestedQuestion : undefined;
  if (
    suggested &&
    !custom.some(
      (question) =>
        question.prompt.trim().toLowerCase() ===
        suggested.prompt.trim().toLowerCase(),
    )
  ) {
    custom = [
      ...custom,
      {
        ...suggested,
        id: suggested.id || createQuestionId(),
        source: 'custom',
        order: custom.length,
      },
    ];
  }

  return {
    ...state,
    entry: { ...state.entry, mode: 'ai' },
    basics,
    content,
    questions: { ...state.questions, custom },
  };
}

// ── Edit-mode hydration (§5.5 / §7.6) ────────────────────────────────────────

/** Server display labels back to canonical option ids (jobs.transforms.ts). */
const COUNTRY_LABEL_TO_ID: Record<string, string> = {
  'united arab emirates': 'uae',
  uae: 'uae',
  'u.a.e.': 'uae',
  'u.a.e': 'uae',
  ksa: 'saudi',
  'saudi arabia': 'saudi',
  qatar: 'qatar',
  oman: 'oman',
  bahrain: 'bahrain',
  kuwait: 'kuwait',
  egypt: 'egypt',
  jordan: 'jordan',
  remote: 'remote',
};

const CITY_LABEL_TO_ID: Record<string, string> = {
  dubai: 'dubai',
  'abu dhabi': 'abu_dhabi',
  sharjah: 'sharjah',
  riyadh: 'riyadh',
  jeddah: 'jeddah',
  doha: 'doha',
  muscat: 'muscat',
  manama: 'manama',
  'kuwait city': 'kuwait_city',
  amman: 'amman',
  cairo: 'cairo',
};

const EMPLOYMENT_API_TO_ID: Record<string, CompanyJobEmployment> = {
  'full-time': 'full_time',
  full_time: 'full_time',
  'part-time': 'part_time',
  part_time: 'part_time',
  contract: 'contract',
  internship: 'internship',
};

/** "Customer Success" / "customer-success" → "customer_success". */
function slugifyOptionValue(value: string): string {
  return value.trim().toLowerCase().replace(/[\s-]+/g, '_');
}

/**
 * Resolves a stored value back to a canonical option id.
 *
 * The server persists DISPLAY labels for country/city ("United Arab
 * Emirates") but the raw id for department, and jobs created outside the
 * wizard (seeds, admin, the agent) store whatever they were given ("UAE",
 * "Product"). Matching only the exact display label left the picker on
 * "Select country" for a job that HAS a country, which invited the employer
 * to overwrite real data with a "fix". Match the canonical ids and the known
 * labels case-insensitively; anything still unrecognised is returned as-is so
 * the field keeps — and shows — what is stored.
 */
function resolveOptionId(
  value: string | null | undefined,
  options: readonly { value: string }[],
  labelToId: Record<string, string> = {},
): string {
  const trimmed = (value ?? '').trim();
  if (!trimmed) return '';
  const mapped = labelToId[trimmed.toLowerCase()];
  if (mapped) return mapped;
  const slug = slugifyOptionValue(trimmed);
  const known = options.find((option) => option.value.toLowerCase() === slug);
  return known ? known.value : trimmed;
}

/** True when the value is one of the picker's own options. */
export function isKnownOptionId(
  value: string,
  options: readonly { value: string }[],
): boolean {
  return options.some((option) => option.value === value);
}

function countryIdFromLabel(value: string | null | undefined): string {
  return resolveOptionId(value, COUNTRY_OPTIONS, COUNTRY_LABEL_TO_ID);
}

function cityIdFromLabel(value: string | null | undefined): string {
  return resolveOptionId(value, CITY_OPTIONS, CITY_LABEL_TO_ID);
}

function departmentIdFromLabel(value: string | null | undefined): string {
  return resolveOptionId(value, DEPARTMENT_OPTIONS);
}

/**
 * Split the flattened free-text `location` ("Dubai, UAE") into its raw parts,
 * exactly as stored. `splitLocation` resolves these to option ids; the raw
 * halves are what a no-op save must be able to write back.
 */
function splitLocationRaw(location: string | null | undefined): {
  city: string;
  country: string;
} {
  const trimmed = (location ?? '').trim();
  if (!trimmed || trimmed.toLowerCase() === 'remote') {
    return { city: '', country: '' };
  }
  const parts = trimmed.split(',').map((part) => part.trim());
  if (parts.length >= 2) {
    return { city: parts[0], country: parts.slice(1).join(', ') };
  }
  if (COUNTRY_LABEL_TO_ID[trimmed.toLowerCase()]) {
    return { city: '', country: trimmed };
  }
  return { city: trimmed, country: '' };
}

function splitLocation(location: string | null | undefined): {
  city: string;
  country: string;
} {
  const raw = splitLocationRaw(location);
  return {
    city: cityIdFromLabel(raw.city),
    country: countryIdFromLabel(raw.country),
  };
}

function normalizeWorkMode(value: string | undefined): CompanyJobWorkType {
  const normalized = (value ?? '')
    .trim()
    .toLowerCase()
    .replace(/[_\s]+/g, '-');
  if (normalized === 'hybrid') return 'hybrid';
  if (normalized === 'remote') return 'remote';
  return 'onsite';
}

/**
 * Prefill the whole draft from an employer-scoped job read so editing uses the
 * same four-step flow (§5.5). Tolerates legacy jobs: missing v2 fields fall
 * back to sensible defaults and the employer sees exactly what is stored.
 */
export function hydrateFromJob(job: CompanyJobWithV2): JobDraftState {
  const base = createInitialJobDraftState();

  const fromColumns = {
    city: cityIdFromLabel(job.city),
    country: countryIdFromLabel(job.country),
  };
  const fromLocation = splitLocation(job.location);
  const city = fromColumns.city || fromLocation.city;
  const country = fromColumns.country || fromLocation.country;
  // Raw stored values, taken from whichever source the ids above came from.
  const rawFromLocation = splitLocationRaw(job.location);
  const storedCity = (job.city ?? '').trim() || rawFromLocation.city;
  const storedCountry = (job.country ?? '').trim() || rawFromLocation.country;

  const salary =
    job.salary && typeof job.salary === 'object' ? job.salary : null;

  const eligibility = job.eligibility;
  const programme = eligibility?.workforceProgramme;

  const v2Questions = job.screeningQuestionsV2 ?? [];
  const defaults = DEFAULT_QUESTION_DEFS.map((def): DefaultQuestionConfig => {
    const stored = v2Questions.find(
      (question) =>
        question.source === 'default' && question.defaultKey === def.key,
    );
    if (!stored) {
      return {
        key: def.key,
        enabled: false,
        required: def.defaultRequired,
        type: def.defaultType,
      };
    }
    return {
      key: def.key,
      enabled: true,
      required: stored.required,
      type: def.compatibleTypes.includes(stored.type)
        ? stored.type
        : def.defaultType,
    };
  });
  const custom = v2Questions
    .filter((question) => question.source !== 'default')
    .slice()
    .sort((a, b) => a.order - b.order);

  const confidential = job.visibility === 'confidential';

  return {
    ...base,
    entry: { mode: 'manual', brief: '' },
    basics: {
      title: job.title,
      department: departmentIdFromLabel(job.department),
      country,
      city,
      workMode: normalizeWorkMode(job.workMode),
      employment: EMPLOYMENT_API_TO_ID[job.type] ?? 'full_time',
      seniority: normalizeSeniority(job.level),
      stored: {
        department: (job.department ?? '').trim(),
        country: storedCountry,
        city: storedCity,
      },
    },
    content: {
      summary: job.description ?? '',
      responsibilities: [...(job.responsibilities ?? [])],
      requirements: [...(job.requirements ?? [])],
      niceToHave: [...(job.niceToHave ?? [])],
      benefits: [...(job.benefits ?? [])],
    },
    compensation: {
      min: typeof salary?.min === 'number' ? String(salary.min) : '',
      max: typeof salary?.max === 'number' ? String(salary.max) : '',
      currency: salary?.currency ?? 'AED',
      period: salary?.period === 'yearly' ? 'yearly' : 'monthly',
      visible: job.salaryVisible !== false,
    },
    eligibility: {
      candidateLocation: normalizeCandidateLocation(
        eligibility?.candidateLocation,
      ),
      experienceYears: eligibility?.experienceYears ?? 'any',
      language: eligibility?.language ?? 'any',
      workEligibility: eligibility?.workEligibility ?? 'any',
      workforceProgramme: programme
        ? {
            value: normalizeNationality(programme.value),
            note: programme.note ?? '',
            acknowledged: programme.acknowledged === true,
          }
        : { value: 'none', note: '', acknowledged: false },
    },
    questions: { defaults, custom },
    // Rows written before `tabbio_only` existed still carry
    // `tabbio_and_company` behind a confidential visibility. Resolving on the
    // way in means the wizard shows where such a role actually appears rather
    // than repeating the stale column back to the employer.
    distribution: resolveDistribution(
      normalizeDistribution(job.distribution),
      confidential,
    ),
    confidential: {
      enabled: confidential,
      publicLabel: confidential ? job.company?.name ?? '' : '',
    },
    lifecycleChoice: job.status === 'draft' ? 'draft' : 'live',
  };
}

// ── Payload assembly ─────────────────────────────────────────────────────────

/**
 * Send the stored value back untouched when the picker still resolves to it.
 *
 * Hydration normalises stored labels to option ids so the pickers can show a
 * selection ("Product" -> "product"). That id must not leak into the PATCH for
 * a field the employer never opened: the API stores department verbatim and
 * the public renderer prints it raw, so a no-op save would visibly downcase a
 * live careers page. When the employer does pick something else the resolved
 * id no longer matches the stored value and the new id is sent as normal.
 */
function preserveStoredValue(
  current: string,
  stored: string | undefined,
  resolve: (value: string | null | undefined) => string,
): string {
  const original = (stored ?? '').trim();
  if (!original) return current;
  return resolve(original) === current ? original : current;
}

function buildBasicsInput(state: JobDraftState): EmployerJobBasicsInput {
  const { basics } = state;
  const stored = basics.stored;
  const department = preserveStoredValue(
    basics.department.trim(),
    stored?.department,
    departmentIdFromLabel,
  );
  const country = preserveStoredValue(
    basics.country,
    stored?.country,
    countryIdFromLabel,
  );
  const city = preserveStoredValue(basics.city, stored?.city, cityIdFromLabel);

  return {
    title: basics.title.trim(),
    ...(department ? { department } : {}),
    ...(country ? { country } : {}),
    ...(city ? { city } : {}),
    workMode: basics.workMode,
    employment: basics.employment,
    seniority: basics.seniority,
  };
}

function buildContentInput(state: JobDraftState): EmployerJobContentInput {
  return {
    summary: state.content.summary.trim(),
    responsibilities: nonEmpty(state.content.responsibilities),
    requirements: nonEmpty(state.content.requirements),
    niceToHave: nonEmpty(state.content.niceToHave),
    benefits: nonEmpty(state.content.benefits),
  };
}

function buildCompensationInput(
  state: JobDraftState,
): EmployerJobCompensationInput | null {
  const min = parseSalary(state.compensation.min);
  const max = parseSalary(state.compensation.max);
  const validMin = typeof min === 'number' ? min : undefined;
  const validMax = typeof max === 'number' ? max : undefined;
  if (validMin === undefined && validMax === undefined) return null;
  return {
    ...(validMin !== undefined ? { min: validMin } : {}),
    ...(validMax !== undefined ? { max: validMax } : {}),
    currency: state.compensation.currency,
    period: state.compensation.period,
    visible: state.compensation.visible,
  };
}

function buildEligibilityInput(
  state: JobDraftState,
): EmployerJobEligibilityInput {
  const { eligibility } = state;
  const programme = eligibility.workforceProgramme;
  return {
    candidateLocation: eligibility.candidateLocation,
    experienceYears: eligibility.experienceYears,
    language: eligibility.language,
    workEligibility: eligibility.workEligibility,
    // Client sends value/note/acknowledged only; the server stamps the actor.
    ...(programme.value !== 'none'
      ? {
          workforceProgramme: {
            value: programme.value,
            note: programme.note.trim(),
            acknowledged: true,
          },
        }
      : {}),
  };
}

function buildQuestionsInput(
  state: JobDraftState,
  resolveText: (key: string) => string,
): JobScreeningQuestionV2[] {
  const defaults = assembleDefaultQuestions(
    state.questions.defaults,
    resolveText,
  );
  const custom = state.questions.custom.map((question) => ({
    id: question.id,
    prompt: question.prompt.trim(),
    type: question.type,
    required: question.required,
    order: 0,
    ...(question.type === 'multiple_choice'
      ? { options: nonEmpty(question.options ?? []) }
      : {}),
    source: 'custom' as const,
  }));
  return [...defaults, ...custom].map((question, index) => ({
    ...question,
    order: index,
  }));
}

export function buildCreatePayload(
  state: JobDraftState,
  saveAs: 'draft' | 'live',
  resolveText: (key: string) => string,
  idempotencyKey?: string,
): CreateEmployerJobInput {
  const compensation = buildCompensationInput(state);
  const confidentialEnabled =
    state.confidential.enabled && isBoardDistribution(state.distribution);
  return {
    ...(idempotencyKey ? { idempotencyKey } : {}),
    saveAs,
    basics: buildBasicsInput(state),
    content: buildContentInput(state),
    ...(compensation ? { compensation } : {}),
    eligibility: buildEligibilityInput(state),
    questions: buildQuestionsInput(state, resolveText),
    distribution: resolveDistribution(state.distribution, confidentialEnabled),
    ...(confidentialEnabled
      ? {
          confidential: {
            enabled: true,
            publicLabel: state.confidential.publicLabel.trim(),
          },
        }
      : {}),
  };
}

export function buildUpdatePayload(
  state: JobDraftState,
  resolveText: (key: string) => string,
  options: { contentUntouched?: boolean } = {},
): UpdateEmployerJobInput {
  const confidentialEnabled =
    state.confidential.enabled && isBoardDistribution(state.distribution);
  return {
    basics: buildBasicsInput(state),
    // `content` is optional on the update contract: leaving untouched content
    // out is what lets a legacy role with an empty responsibilities or
    // qualifications list be edited at all — the server would reject those
    // arrays, and re-sending them adds nothing.
    ...(options.contentUntouched ? {} : { content: buildContentInput(state) }),
    // null clears a previously stored salary; an object replaces it.
    compensation: buildCompensationInput(state),
    eligibility: buildEligibilityInput(state),
    questions: buildQuestionsInput(state, resolveText),
    distribution: resolveDistribution(state.distribution, confidentialEnabled),
    confidential: confidentialEnabled
      ? { enabled: true, publicLabel: state.confidential.publicLabel.trim() }
      : { enabled: false },
  };
}
